import { createElement, type ReactNode } from 'react';

/**
 * NO-BREAK NUMBERS (owner rule, Andrew Pawlak, 2026-10-07: "ALWAYS make sure numbers don't break across lines").
 * A figure such as 50-100+, 6-12, $1,200-$2,000, 5-12%, 3-5X, +$274,000 or ~18 must never be split over two lines. Browsers break after a
 * hyphen or en dash, so each numeric token is wrapped in a white-space:nowrap span. The text itself is unchanged (search engines and
 * screen readers read the same characters); only the line-break behaviour changes. Used by the MDX build (lib/rehype-nobreak.ts) and by
 * components that print string props (nb()). Verified by scripts/page-gates/number-break-check.mjs.
 * Deliberately no regex lookbehind (older Safari throws on it): the character before a token is captured and re-emitted instead.
 */
const SRC = '(^|[^A-Za-z0-9])((?:[A-Za-z]{2,5}-)?[~+]?\\.?\\$?\\d[\\d,]*(?:\\.\\d+)?(?:\\s?[-\\u2013]\\s?\\$?\\d[\\d,]*(?:\\.\\d+)?)?(?:[A-Za-z]{1,3}\\b)?[%+]*(?:-[A-Za-z]{2,12}){0,2})';

export type NumberPart = { text: string; number: boolean };

export function splitNumbers(text: string): NumberPart[] {
  const re = new RegExp(SRC, 'g');
  const parts: NumberPart[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const pre = m[1] || '';
    const tok = m[2];
    if (!/\d/.test(tok)) continue;
    const start = m.index + pre.length;
    if (start > last) parts.push({ text: text.slice(last, start), number: false });
    parts.push({ text: tok, number: true });
    last = start + tok.length;
    if (tok.length === 0) re.lastIndex++;
  }
  if (last < text.length) parts.push({ text: text.slice(last), number: false });
  return parts;
}

export const NO_BREAK_STYLE = { whiteSpace: 'nowrap' } as const;

/** Returns the string with every numeric token wrapped so it cannot split across lines (plain string when it has no numbers). */
export function nb(text: ReactNode): ReactNode {
  if (typeof text !== 'string' || !text) return text;
  const parts = splitNumbers(text);
  if (!parts.some((p) => p.number)) return text;
  return parts.map((p, i) => (p.number ? createElement('span', { key: i, style: NO_BREAK_STYLE }, p.text) : p.text));
}
